import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { LandingLayout } from '@/pages/landing/shared/LandingLayout';
import { AdminLayout } from '@/layouts/admin-layout';
import { useAuth } from '@/contexts/auth-context';
import { PricingTable } from '@/components/subscription/PricingTable';
import { subscriptionService } from '@/services/api/subscription.service';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, Mail } from 'lucide-react';

const SITE_URL = 'https://slotyapp.com';

function detectCurrency(): string {
  try {
    const lang = navigator.language?.toLowerCase() || '';
    if (lang === 'pt-br' || lang.startsWith('pt-br')) return 'brl';
    if (lang.startsWith('en-us') || lang.startsWith('en-ca')) return 'usd';
    // Check timezone for more accuracy
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (timezone.startsWith('America/Sao_Paulo') || timezone.startsWith('America/Fortaleza') ||
        timezone.startsWith('America/Recife') || timezone.startsWith('America/Bahia') ||
        timezone.startsWith('America/Belem') || timezone.startsWith('America/Manaus')) {
      return 'brl';
    }
    if (timezone.startsWith('America/New_York') || timezone.startsWith('America/Chicago') ||
        timezone.startsWith('America/Denver') || timezone.startsWith('America/Los_Angeles')) {
      return 'usd';
    }
  } catch {
    // ignore detection errors
  }
  return 'eur'; // default for European users
}

export default function PricingPage() {
  const { t } = useTranslation('billing');
  const { user } = useAuth();
  const currency = useMemo(() => detectCurrency(), []);

  // Fetch current subscription if user is logged in
  const { data: subscriptionData } = useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: () => subscriptionService.getCurrentSubscription(),
    enabled: !!user,
  });

  const hasActiveSubscription = subscriptionData?.subscription?.status === 'active';

  // Choose layout based on auth status
  const Layout = user ? AdminLayout : LandingLayout;

  const pricingContent = (
    <div className={user ? '' : 'min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800'}>
      <Helmet>
        <title>{t('pricing.seoTitle')}</title>
        <meta name="title" content={t('pricing.seoTitle')} />
        <meta name="description" content={t('pricing.seoDescription')} />
        <link rel="canonical" href={`${SITE_URL}/pricing`} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/pricing`} />
        <meta property="og:title" content={t('pricing.seoTitle')} />
        <meta property="og:description" content={t('pricing.seoDescription')} />
        <meta property="og:image" content={`${SITE_URL}/og-image.jpg`} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('pricing.seoTitle')} />
        <meta name="twitter:description" content={t('pricing.seoDescription')} />

        {/* Structured Data - Product Offers */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Sloty Booking Management",
            "description": "Smart booking management software for service businesses",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "brand": {
              "@type": "Brand",
              "name": "Sloty"
            },
            "offers": [
              {
                "@type": "Offer",
                "name": "Basic Plan",
                "price": "30",
                "priceCurrency": "USD",
                "priceValidUntil": "2026-12-31",
                "availability": "https://schema.org/InStock"
              },
              {
                "@type": "Offer",
                "name": "Professional Plan",
                "price": "65",
                "priceCurrency": "USD",
                "priceValidUntil": "2026-12-31",
                "availability": "https://schema.org/InStock"
              },
              {
                "@type": "Offer",
                "name": "Enterprise Plan",
                "price": "199",
                "priceCurrency": "USD",
                "priceValidUntil": "2026-12-31",
                "availability": "https://schema.org/InStock"
              }
            ]
          })}
        </script>

        {/* FAQ Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can I change plans later?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we'll prorate the difference."
                }
              },
              {
                "@type": "Question",
                "name": "What happens after my trial ends?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Your subscription will automatically start and your payment method will be charged. You can cancel anytime during the trial with no charge."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer refunds?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, we offer a 30-day money-back guarantee on all paid plans. If you're not satisfied, contact us for a full refund."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={user ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16'}>
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            <h1 className={`${user ? 'text-2xl sm:text-3xl' : 'text-3xl sm:text-4xl'} font-bold text-gray-900 dark:text-white mb-3 sm:mb-4`}>
              {t('pricing.pageTitle')}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-4">
              {t('pricing.pageSubtitle')}
            </p>
          </div>

          {/* Pricing Table for all users */}
          {user && hasActiveSubscription ? (
            <Card className="max-w-2xl mx-auto mb-12">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  {t('pricing.activeSubscription.title')}
                </CardTitle>
                <CardDescription>
                  {t('pricing.activeSubscription.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Button asChild>
                    <a href="/admin/billing">
                      {t('pricing.activeSubscription.manageBilling')}
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <PricingTable
              currency={currency}
              interval="year"
              isSubscribed={false}
              className="mb-12"
            />
          )}

          {/* FAQ Section */}
          <div className={`${user ? 'mt-10 sm:mt-12' : 'mt-16'} text-center`}>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8">
              {t('pricing.faq.title')}
            </h2>
            <div className="max-w-3xl mx-auto space-y-5 sm:space-y-6 text-left">
              <div className="bg-white dark:bg-gray-800 p-5 sm:p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-base sm:text-lg text-gray-900 dark:text-white mb-2">
                  {t('pricing.faq.changePlans.question')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  {t('pricing.faq.changePlans.answer')}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-5 sm:p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-base sm:text-lg text-gray-900 dark:text-white mb-2">
                  {t('pricing.faq.trialEnd.question')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  {t('pricing.faq.trialEnd.answer')}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-5 sm:p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-base sm:text-lg text-gray-900 dark:text-white mb-2">
                  {t('pricing.faq.refunds.question')}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                  {t('pricing.faq.refunds.answer')}
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className={`${user ? 'mt-10 sm:mt-12 pb-4' : 'mt-16'} text-center`}>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-4">
              {t('pricing.helpChoosing')}
            </p>
            <Button
              variant="outline"
              size="lg"
              className="shadow-sm hover:shadow-md transition-shadow inline-flex items-center gap-2"
              onClick={() => window.location.href = 'mailto:sales@slotyapp.com?subject=Pricing Inquiry'}
            >
              <Mail className="w-4 h-4" />
              {t('pricing.contactSales')}
            </Button>
          </div>
        </div>
      </div>
  );

  return <Layout>{pricingContent}</Layout>;
}
